import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, CheckCircle2, Mail, ExternalLink } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useDocumentMeta({
    title: "Privacy Policy | TeleMedCare - Secure Telehealth Nigeria",
    description: "Learn how TeleMedCare protects your health and personal data. Our privacy standards comply with the NDPA 2023 and HIPAA-aligned security controls.",
    canonical: "https://telemedcare.app/privacy",
  });

  const sections = [
    { id: "intro", title: "1. Introduction & Scope" },
    { id: "collect", title: "2. Information We Collect" },
    { id: "usage", title: "3. How We Use Your Information" },
    { id: "sharing", title: "4. Data Sharing & Consent" },
    { id: "security", title: "5. Security & Data Protection" },
    { id: "rights", title: "6. Your Rights & Data Deletion" },
    { id: "retention", title: "7. Data Retention" },
    { id: "contact", title: "8. Contact DPO & Support" },
  ];

  return (
    <div className="min-h-screen bg-cream text-ink">
      <Navbar />

      <main className="pt-[72px]">
        {/* Header Hero Section */}
        <section className="bg-ink text-cream py-16 lg:py-24 relative overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute -top-32 right-0 h-[400px] w-[400px] rounded-full bg-forest/25 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-32 left-0 h-[300px] w-[300px] rounded-full bg-clay/20 blur-3xl"
          />
          <div className="container-x relative">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-forest/35 border border-forest/50 px-3.5 py-1.5 text-[12.5px] font-semibold uppercase tracking-[0.18em] text-cream">
                <Lock className="h-3.5 w-3.5 text-clay" />
                Data Protection
              </span>
              <h1 className="mt-5 font-display text-[40px] sm:text-[52px] font-extrabold leading-[1.1] tracking-tight">
                Privacy Policy
              </h1>
              <p className="mt-4 text-[16px] sm:text-[18px] text-cream/80 leading-relaxed">
                Last updated: June 9, 2026. How Hivebrain LTD (“TeleMedCare”) collects, safeguards, and handles your personal, clinical, and chat information.
              </p>
            </div>
          </div>
        </section>

        {/* Core Layout - Sidebar + Content */}
        <section className="py-16 lg:py-24">
          <div className="container-x">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              
              {/* Sticky Sidebar Navigation */}
              <nav className="lg:col-span-4 sticky top-28 hidden lg:block bg-white border border-border/40 rounded-3xl p-6 shadow-[0_8px_32px_rgba(14,35,51,0.02)]">
                <p className="text-[12px] uppercase tracking-[0.18em] text-ink/50 font-semibold mb-4">
                  Table of Contents
                </p>
                <ul className="space-y-3.5">
                  {sections.map((sec) => (
                    <li key={sec.id}>
                      <a
                        href={`#${sec.id}`}
                        className="block text-[14.5px] text-ink/70 hover:text-forest transition-colors font-medium border-l-2 border-transparent hover:border-forest/40 pl-3 leading-snug"
                      >
                        {sec.title}
                      </a>
                    </li>
                  ))}
                </ul>

                <hr className="my-6 border-border/60" />

                <div className="rounded-2xl bg-cream/70 p-4 border border-border/40">
                  <div className="flex gap-2 text-forest text-[13px] font-bold uppercase tracking-wider items-center">
                    <ShieldCheck className="h-4 w-4" />
                    Secure Infrastructure
                  </div>
                  <p className="mt-2 text-[12.5px] text-ink/65 leading-relaxed">
                    We process all medical records in compliance with the Nigeria Data Protection Act (NDPA) 2023.
                  </p>
                </div>
              </nav>

              {/* Main Policy Content */}
              <div className="lg:col-span-8 max-w-3xl">
                
                {/* Visual Highlights Box */}
                <div className="mb-12 rounded-3xl bg-forest text-cream p-7 lg:p-9 shadow-lg relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-8 opacity-10">
                    <Lock className="h-32 w-32" />
                  </div>
                  <h2 className="font-display text-[22px] font-extrabold tracking-tight mb-4">
                    Our Privacy Commitment to You
                  </h2>
                  <p className="text-[15.5px] text-cream/90 leading-relaxed mb-6">
                    As a modern healthcare platform, we treat your medical and chat records with absolute discretion. Here is what you can always expect:
                  </p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <li className="flex gap-2.5 items-start">
                      <CheckCircle2 className="h-4.5 w-4.5 text-clay shrink-0 mt-0.5" />
                      <span className="text-[14.5px] text-cream/90">We never sell your medical data</span>
                    </li>
                    <li className="flex gap-2.5 items-start">
                      <CheckCircle2 className="h-4.5 w-4.5 text-clay shrink-0 mt-0.5" />
                      <span className="text-[14.5px] text-cream/90">End-to-end encrypted databases</span>
                    </li>
                    <li className="flex gap-2.5 items-start">
                      <CheckCircle2 className="h-4.5 w-4.5 text-clay shrink-0 mt-0.5" />
                      <span className="text-[14.5px] text-cream/90">Clinician review on all AI summaries</span>
                    </li>
                    <li className="flex gap-2.5 items-start">
                      <CheckCircle2 className="h-4.5 w-4.5 text-clay shrink-0 mt-0.5" />
                      <span className="text-[14.5px] text-cream/90">Right to request full data deletion</span>
                    </li>
                  </ul>
                </div>

                {/* Section Content */}
                <article className="prose prose-slate max-w-none">
                  
                  {/* Intro */}
                  <section id="intro" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      1. Introduction & Scope
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      Hivebrain LTD (“TeleMedCare”, “we”, “our”, or “us”) operates the website <strong>telemedcare.app</strong> and the clinical workflow interfaces including our WhatsApp-based triage and consulting flows (collectively, the “Platform”).
                    </p>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      This Privacy Policy explains how we collect, store, share, and process your personal and health information when you interact with our Platform, communicate with our clinical network on WhatsApp, or register for our service.
                    </p>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed">
                      By accessing the Platform or chatting with a clinician on our WhatsApp care line, you acknowledge that you have read and understood the terms of this Privacy Policy.
                    </p>
                  </section>

                  {/* Information We Collect */}
                  <section id="collect" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      2. Information We Collect
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      We collect information to provide you with secure, high-quality, and legally compliant medical consultation services. This includes:
                    </p>
                    
                    <h3 className="font-display text-[18px] font-bold text-ink mb-2">A. Registration & Profile Data</h3>
                    <p className="text-[15px] text-ink/70 leading-relaxed mb-4">
                      When signing up, you provide your name, phone number, email address, date of birth, biological sex, and geographic location.
                    </p>

                    <h3 className="font-display text-[18px] font-bold text-ink mb-2">B. Medical & Clinical Data</h3>
                    <p className="text-[15px] text-ink/70 leading-relaxed mb-4">
                      To help clinicians diagnose and treat you, we collect and store:
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-[15px] text-ink/70 space-y-2">
                      <li>Your described symptoms, medical history, allergies, and current medications.</li>
                      <li>Diagnostic records, lab results, and photos of symptoms (e.g. rashes, wounds) shared during consultations.</li>
                      <li>Doctor session transcripts, consultation notes, and copies of issued prescriptions.</li>
                    </ul>

                    <h3 className="font-display text-[18px] font-bold text-ink mb-2">C. WhatsApp Chat & Metadata</h3>
                    <p className="text-[15px] text-ink/70 leading-relaxed mb-4">
                      Because our Platform integrates with WhatsApp, we store the messages, images, audio clips, and files you send to our official care contact (+234 810 544 4731) on our secure servers, alongside your WhatsApp contact information.
                    </p>

                    <h3 className="font-display text-[18px] font-bold text-ink mb-2">D. Billing & Transaction Data</h3>
                    <p className="text-[15px] text-ink/70 leading-relaxed">
                      All consultation payments are processed via secured third-party payment gateways (e.g. Paystack or Flutterwave). We store payment status and transaction ID records but do not store your raw credit card number or bank details.
                    </p>
                  </section>

                  {/* How We Use Your Information */}
                  <section id="usage" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      3. How We Use Your Information
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      We use your information strictly to operate the Platform, deliver medical consultations, and ensure clinical excellence:
                    </p>
                    <ul className="list-decimal pl-5 mb-4 text-[15px] text-ink/70 space-y-3">
                      <li>
                        <strong>Clinical Delivery:</strong> Connecting you to independently licensed Nigerian physicians for advice, prescriptions, and lab referrals.
                      </li>
                      <li>
                        <strong>AI Triage & Documentation Support:</strong> We use HIPAA-aligned AI tools to analyze your described symptoms before you see a doctor (AI Symptom Check) and to draft visit summaries for your doctor's review (AI Scribe). <em>Importantly, no medical decisions are made solely by AI — every summary, triage result, and care plan is verified and authorized by a licensed human clinician.</em>
                      </li>
                      <li>
                        <strong>Prescriptions & Lab Orders:</strong> Processing medication deliveries through partner pharmacies and scheduling diagnostic panels with partner labs.
                      </li>
                      <li>
                        <strong>Operations & Safety:</strong> Verifying user identities, conducting quality assurance reviews of clinical chats, and protecting our platform from fraud.
                      </li>
                    </ul>
                  </section>

                  {/* Data Sharing & Consent */}
                  <section id="sharing" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      4. Data Sharing & Consent
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      Your medical logs are highly sensitive. We only share them in the following circumstances:
                    </p>
                    
                    <h3 className="font-display text-[17px] font-bold text-ink mb-2">A. With Your Direct Care Team</h3>
                    <p className="text-[15px] text-ink/70 leading-relaxed mb-4">
                      Vetted, licensed clinicians assigned to your consultation will have full access to your medical history on our electronic medical records (EMR) interface to make safe clinical choices.
                    </p>

                    <h3 className="font-display text-[17px] font-bold text-ink mb-2">B. With Pharmacies and Labs</h3>
                    <p className="text-[15px] text-ink/70 leading-relaxed mb-4">
                      If your doctor writes a prescription or requests a lab test, we share relevant details (name, contact, prescription contents) with our fulfillment partners to complete your request.
                    </p>

                    <h3 className="font-display text-[17px] font-bold text-ink mb-2">C. Legal Compliance & Harm Prevention</h3>
                    <p className="text-[15px] text-ink/70 leading-relaxed mb-6">
                      We may share records to comply with legal processes (subpoenas, medical board requests) or if we reasonably believe disclosure is necessary to protect the life or safety of a patient or others.
                    </p>

                    <div className="rounded-2xl border border-clay/30 bg-clay/5 p-5">
                      <p className="text-[14px] text-ink font-semibold flex items-center gap-1.5 text-clay">
                        No Third-Party Advertising
                      </p>
                      <p className="mt-1 text-[13.5px] text-ink/75 leading-relaxed">
                        We never sell, rent, or trade your medical history, diagnosis, contact info, or messaging details to pharmaceutical companies, insurers, or marketing networks.
                      </p>
                    </div>
                  </section>

                  {/* Security & Data Protection */}
                  <section id="security" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      5. Security & Data Protection
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      We implement strict safeguards, mirroring requirements outlined in the <strong>Nigeria Data Protection Act (NDPA) 2023</strong> and the US **Health Insurance Portability and Accountability Act (HIPAA)** guidelines:
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-[15px] text-ink/70 space-y-2">
                      <li><strong>Encryption:</strong> Data is encrypted during transmission (SSL/TLS) and at rest on secure cloud servers.</li>
                      <li><strong>Access Controls:</strong> Multi-factor authentication and role-based permissions ensure only licensed care professionals and essential engineering staff can access health data.</li>
                      <li><strong>Infrastructure Audit:</strong> Regular security assessments, log audits, and system scanning.</li>
                    </ul>
                  </section>

                  {/* Your Rights & Data Deletion */}
                  <section id="rights" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      6. Your Rights & Data Deletion
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      You have full ownership of your data under Nigerian data protection guidelines. You may exercise these rights at any time:
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-[15px] text-ink/70 space-y-2">
                      <li><strong>Access:</strong> Request a complete summary of your medical logs and records.</li>
                      <li><strong>Correction:</strong> Ask us to update incorrect profile or contact details.</li>
                      <li><strong>Data Deletion:</strong> Request the deletion of your account and all associated personal records.</li>
                    </ul>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      To request deletion, you can review our detailed instructions on the{" "}
                      <Link to="/delete-account" className="text-forest hover:underline font-semibold inline-flex items-center gap-0.5">
                        Data Deletion Instructions Page
                        <ExternalLink className="h-3.5 w-3.5 inline" />
                      </Link>{" "}
                      or directly email us at{" "}
                      <a href="mailto:hello@telemedcare.app" className="text-forest hover:underline font-semibold">
                        hello@telemedcare.app
                      </a>.
                    </p>
                    <p className="text-[15px] text-ink/70 leading-relaxed bg-cream/80 border border-border/60 p-4 rounded-xl">
                      <em>Note: Medical record retention regulations may require us or our clinical network partners to archive core clinical chart summaries for the legal duration mandated under healthcare laws, even after profile deletion is requested.</em>
                    </p>
                  </section>

                  {/* Data Retention */}
                  <section id="retention" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      7. Data Retention
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      We store your profile and message logs for as long as you maintain an active account on the Platform.
                    </p>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed">
                      If you close your account, we will erase or permanently anonymize your data within 7 business days, except where clinical record retention policies require archiving.
                    </p>
                  </section>

                  {/* Contact DPO */}
                  <section id="contact" className="scroll-mt-24 mb-6">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      8. Contact DPO & Support
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      If you have questions about this Privacy Policy, your medical files, or data protection practices, please contact our Data Protection Officer:
                    </p>
                    <div className="rounded-3xl border border-border/60 bg-white p-6 shadow-sm">
                      <p className="font-display text-[16px] font-bold text-ink mb-1">
                        Hivebrain LTD (Data Protection Officer)
                      </p>
                      <p className="text-[14.5px] text-ink/70 mb-3">
                        Lagos, Nigeria
                      </p>
                      <a
                        href="mailto:hello@telemedcare.app"
                        className="inline-flex items-center gap-2 text-forest hover:text-forestHover font-semibold text-[14.5px]"
                      >
                        <Mail className="h-4.5 w-4.5" />
                        hello@telemedcare.app
                      </a>
                    </div>
                  </section>

                </article>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
